import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Loader2, Lock, CheckCircle, AlertTriangle, CreditCard, X } from "lucide-react";
import { getStripe } from "@/lib/stripe";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable Stripe Elements payment surface.
 *
 * Usage:
 *   <StripePaymentModal
 *     open
 *     onClose={...}
 *     onSuccess={({ paymentIntentId }) => ...}
 *     intent={{
 *       endpoint: "/payments/package/intent",
 *       body: { package_key, email, name, mode: "full"|"deposit", booking_id? },
 *     }}
 *     title="Book Studio Day"
 *     subtitle="$605 · secure card payment"
 *   />
 */
const currency = (cents) => `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CheckoutForm = ({ paymentIntentId, amountCents, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError("");
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message || "Payment couldn't be submitted");
        setSubmitting(false);
        return;
      }
      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
        confirmParams: {
          return_url: `${window.location.origin}/payment/success?pi=${paymentIntentId}`,
        },
      });
      if (confirmError) {
        setError(confirmError.message || "Payment failed. Please try a different card.");
        setSubmitting(false);
        return;
      }
      if (paymentIntent && paymentIntent.status === "succeeded") {
        setDone(true);
        // Give the UI a beat before closing so the user sees success
        setTimeout(() => onSuccess({ paymentIntentId: paymentIntent.id }), 900);
      } else if (paymentIntent && paymentIntent.status === "processing") {
        setDone(true);
        setTimeout(() => onSuccess({ paymentIntentId: paymentIntent.id, processing: true }), 900);
      } else {
        setError("Payment is still pending. We'll email you when it clears.");
        setSubmitting(false);
      }
    } catch (err) {
      setError(err?.message || "Unexpected error — please try again");
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-8" data-testid="payment-success">
        <div className="w-14 h-14 rounded-full bg-[#22c55e]/15 border border-[#22c55e]/40 text-[#22c55e] flex items-center justify-center mx-auto mb-4">
          <CheckCircle size={24} />
        </div>
        <h3 className="font-display font-black text-2xl tracking-tight">Payment received.</h3>
        <p className="text-[#a395c3] text-sm mt-2">
          {currency(amountCents)} confirmed. A receipt is on its way.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="stripe-elements-form">
      <PaymentElement options={{ layout: "tabs" }} />

      {error && (
        <div
          role="alert"
          data-testid="payment-error"
          className="flex items-start gap-2 px-3 py-2 rounded-lg bg-[#ff0055]/10 border border-[#ff0055]/40 text-[#ff0055] text-xs"
        >
          <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex items-center gap-2 text-[10px] text-white/40 uppercase tracking-[0.24em]">
        <Lock size={10} /> Secured by Stripe · No card data touches our servers
      </div>

      <div className="flex items-center gap-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="px-4 py-3 rounded-full bg-white/[0.05] border border-white/15 text-sm hover:bg-white/[0.1] disabled:opacity-50"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={!stripe || submitting}
          data-testid="stripe-pay-btn"
          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#00f0ff] text-black font-bold text-sm hover:bg-[#00f0ff]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {submitting ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Processing…
            </>
          ) : (
            <>
              <CreditCard size={14} /> Pay {currency(amountCents)}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

const StripePaymentModal = ({ open, onClose, onSuccess, intent, title, subtitle }) => {
  const [clientSecret, setClientSecret] = useState("");
  const [paymentIntentId, setPaymentIntentId] = useState("");
  const [amountCents, setAmountCents] = useState(0);
  const [loadingIntent, setLoadingIntent] = useState(false);
  const [intentError, setIntentError] = useState("");

  const stripePromise = useMemo(() => getStripe(), []);
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!open || !intent?.endpoint) return;
    // Guard against React StrictMode double-invocation + rapid re-opens.
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setClientSecret("");
    setIntentError("");
    setLoadingIntent(true);
    (async () => {
      try {
        const { data } = await axios.post(`${API}${intent.endpoint}`, intent.body);
        setClientSecret(data.client_secret);
        setPaymentIntentId(data.payment_intent_id);
        setAmountCents(data.amount_cents || 0);
      } catch (e) {
        setIntentError(e?.response?.data?.detail || "Failed to start the payment. Try again in a moment.");
      } finally {
        setLoadingIntent(false);
        inFlightRef.current = false;
      }
    })();
    // Only refetch when the modal opens or the endpoint changes.
  }, [open, intent?.endpoint]);

  // Reset the guard when the modal fully closes so it can reopen later
  useEffect(() => {
    if (!open) inFlightRef.current = false;
  }, [open]);

  if (!open) return null;

  const stripeOptions = clientSecret
    ? {
        clientSecret,
        appearance: {
          theme: "night",
          variables: {
            colorPrimary: "#00f0ff",
            colorBackground: "#0a0510",
            colorText: "#ffffff",
            colorDanger: "#ff0055",
            fontFamily: "Inter, system-ui, sans-serif",
            borderRadius: "12px",
          },
        },
      }
    : null;

  return (
    <div
      className="fixed inset-0 z-[10000] bg-black/80 backdrop-blur-sm p-4 md:p-8 overflow-y-auto"
      onClick={onClose}
      data-testid="stripe-payment-modal"
    >
      <div
        className="max-w-md mx-auto rounded-3xl bg-[#0a0510] border border-white/10 p-6 md:p-7"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#00f0ff] mb-1">
              Secure checkout
            </div>
            <h2 className="font-display font-black text-2xl tracking-tight leading-tight">{title}</h2>
            {subtitle && <p className="text-[#a395c3] text-sm mt-2">{subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white/[0.05] border border-white/15 hover:bg-white/[0.12] flex items-center justify-center flex-shrink-0"
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>

        {loadingIntent && (
          <div className="py-10 text-center text-white/60 text-sm">
            <Loader2 size={16} className="inline animate-spin mr-2" />
            Preparing secure payment…
          </div>
        )}
        {intentError && (
          <div className="py-8 text-center">
            <AlertTriangle size={22} className="mx-auto mb-2 text-[#ff0055]" />
            <p className="text-[#ff0055] text-sm">{intentError}</p>
          </div>
        )}
        {clientSecret && stripePromise && (
          <Elements stripe={stripePromise} options={stripeOptions}>
            <CheckoutForm
              paymentIntentId={paymentIntentId}
              amountCents={amountCents}
              onSuccess={onSuccess}
              onCancel={onClose}
            />
          </Elements>
        )}
      </div>
    </div>
  );
};

export default StripePaymentModal;
